import { useEffect, useState, useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { startOfToday } from 'date-fns'
import { z } from 'zod'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useInfiniteEventsList } from '@/stores/events.store'
import { EventsTable } from '@/components/events/events-table'
import { mapEventLiteToDisplay } from '@/components/events/types'
import { Empty, EmptyMedia, EmptyDescription } from '@/components/ui/empty'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { NewEventSheet } from '@/components/events/new-event-sheet'
import { LoadMoreBar } from '@/components/shared/load-more-bar'
import { Calendar, Plus } from 'lucide-react'
import { EventListBucketEnum } from '@/gen/cortado/v1/settings_pb'

export const Route = createFileRoute('/_app/_home/events/')({
  component: EventsPage,
  validateSearch: z.object({
    tab: z.enum(['upcoming', 'completed']).catch('upcoming'),
  }),
  // Events are fetched by the page via useInfiniteEventsList (paginated).
  // connect-query doesn't expose infinite-query options to prefetch in a
  // loader, so there's nothing to warm here.
})

function EventsPage() {
  useBreadcrumbFromRoute()
  const [createSheetOpen, setCreateSheetOpen] = useState(false)
  const [contactNameFilter, setContactNameFilter] = useState('')
  // Archived is a scope toggle shared by BOTH bucket queries, so it lives here
  // rather than in either table's column-filter state.
  const [includeArchived, setIncludeArchived] = useState(false)
  const debouncedContactNameFilter = useDebouncedValue(contactNameFilter.trim(), 300)
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()

  const today = useTodayBoundary()

  // Upcoming and Completed are each their own server-filtered, independently
  // paginated query — the page ordinarily has far more past events than fit
  // in one page, so a single unfiltered query's first page can be entirely
  // past events, leaving the Upcoming tab empty.
  const {
    events: upcomingEventsRaw,
    totalCount: upcomingTotalCount,
    hasMore: upcomingHasMore,
    fetchNextPage: fetchNextUpcomingPage,
    isFetchingNextPage: isFetchingNextUpcomingPage,
    isLoading: isUpcomingLoading,
    isFetching: isUpcomingFetching,
    isPlaceholderData: isUpcomingPlaceholderData,
    isError: isUpcomingError,
    error: upcomingError,
    hasLoaded: hasLoadedUpcoming,
  } = useInfiniteEventsList({
    contactNameFilter: debouncedContactNameFilter || undefined,
    listBucket: EventListBucketEnum.UPCOMING,
    listBucketBoundary: today,
    includeArchived: includeArchived || undefined,
  })

  const {
    events: completedEventsRaw,
    totalCount: completedTotalCount,
    hasMore: completedHasMore,
    fetchNextPage: fetchNextCompletedPage,
    isFetchingNextPage: isFetchingNextCompletedPage,
    isLoading: isCompletedLoading,
    isFetching: isCompletedFetching,
    isPlaceholderData: isCompletedPlaceholderData,
    isError: isCompletedError,
    error: completedError,
    hasLoaded: hasLoadedCompleted,
  } = useInfiniteEventsList({
    contactNameFilter: debouncedContactNameFilter || undefined,
    listBucket: EventListBucketEnum.COMPLETED,
    listBucketBoundary: today,
    includeArchived: includeArchived || undefined,
  })

  const isFilterLoading =
    contactNameFilter.trim() !== debouncedContactNameFilter ||
    (isUpcomingFetching && isUpcomingPlaceholderData) ||
    (isCompletedFetching && isCompletedPlaceholderData)

  const upcomingEvents = useMemo(
    () => (isFilterLoading || isUpcomingPlaceholderData ? [] : upcomingEventsRaw.map(mapEventLiteToDisplay)),
    [isFilterLoading, isUpcomingPlaceholderData, upcomingEventsRaw]
  )
  const completedEvents = useMemo(
    () => (isFilterLoading || isCompletedPlaceholderData ? [] : completedEventsRaw.map(mapEventLiteToDisplay)),
    [completedEventsRaw, isCompletedPlaceholderData, isFilterLoading]
  )

  const isLoading = isUpcomingLoading || isCompletedLoading
  const hasLoaded = hasLoadedUpcoming && hasLoadedCompleted
  const isError = isUpcomingError || isCompletedError
  const error = upcomingError || completedError

  const handleTabChange = (newTab: string) => {
    navigate({ search: { tab: newTab as 'upcoming' | 'completed' } })
  }

  if (isLoading || !hasLoaded) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <Empty className="min-h-[400px]">
          <EmptyMedia variant="icon">
            <Calendar className="h-10 w-10" />
          </EmptyMedia>
          <EmptyDescription>
            Loading events...
          </EmptyDescription>
        </Empty>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <Empty className="min-h-[400px]">
          <EmptyMedia variant="icon">
            <Calendar className="h-10 w-10" />
          </EmptyMedia>
          <EmptyDescription>
            Error loading events: {error?.message || 'Unknown error'}
          </EmptyDescription>
        </Empty>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <Tabs value={tab} onValueChange={handleTabChange} className="flex flex-col h-full">
        <div className="flex items-center justify-between">
          <TabsList className="w-fit">
            <TabsTrigger value="upcoming">
              Upcoming ({isFilterLoading ? '…' : upcomingTotalCount})
            </TabsTrigger>
            <TabsTrigger value="completed">
              Completed ({isFilterLoading ? '…' : completedTotalCount})
            </TabsTrigger>
          </TabsList>

          <Button onClick={() => setCreateSheetOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Event
          </Button>
        </div>

        <TabsContent value="upcoming" className="mt-4 space-y-3">
          <EventsTable
            data={upcomingEvents}
            initialSort={[{ id: 'eventDate', desc: false }]}
            contactNameFilter={contactNameFilter}
            onContactNameFilterChange={setContactNameFilter}
            includeArchived={includeArchived}
            onIncludeArchivedChange={setIncludeArchived}
            isFilterLoading={isFilterLoading}
            paginationResetKey={`upcoming:${debouncedContactNameFilter}:${includeArchived}`}
          />
          <LoadMoreBar
            loaded={upcomingEventsRaw.length}
            total={upcomingTotalCount}
            hasMore={!isFilterLoading && !isUpcomingPlaceholderData && upcomingHasMore}
            isLoading={isFetchingNextUpcomingPage}
            onLoadMore={() => fetchNextUpcomingPage()}
          />
        </TabsContent>

        <TabsContent value="completed" className="mt-4 space-y-3">
          <EventsTable
            data={completedEvents}
            initialSort={[{ id: 'eventDate', desc: true }]}
            contactNameFilter={contactNameFilter}
            onContactNameFilterChange={setContactNameFilter}
            includeArchived={includeArchived}
            onIncludeArchivedChange={setIncludeArchived}
            isFilterLoading={isFilterLoading}
            paginationResetKey={`completed:${debouncedContactNameFilter}:${includeArchived}`}
          />
          <LoadMoreBar
            loaded={completedEventsRaw.length}
            total={completedTotalCount}
            hasMore={!isFilterLoading && !isCompletedPlaceholderData && completedHasMore}
            isLoading={isFetchingNextCompletedPage}
            onLoadMore={() => fetchNextCompletedPage()}
          />
        </TabsContent>
      </Tabs>

      {/* Create Event Sheet */}
      <NewEventSheet
        open={createSheetOpen}
        onOpenChange={setCreateSheetOpen}
      />
    </div>
  )
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedValue(value), delayMs)
    return () => clearTimeout(timeoutId)
  }, [delayMs, value])

  return debouncedValue
}

function useTodayBoundary(): Date {
  const [boundary, setBoundary] = useState(() => startOfToday())

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>
    const scheduleNextDay = () => {
      const nextBoundary = new Date()
      nextBoundary.setHours(24, 0, 0, 0)
      timeoutId = setTimeout(() => {
        setBoundary(startOfToday())
        scheduleNextDay()
      }, Math.max(1, nextBoundary.getTime() - Date.now()))
    }
    scheduleNextDay()
    return () => clearTimeout(timeoutId)
  }, [])

  return boundary
}
